import { motion } from "framer-motion";
import { ServerCog, PackageSearch, Handshake, Fuel, Zap, Truck, ArrowUpRight } from "lucide-react";

const CARDS = [
  {
    icon: ServerCog,
    tag: "DIV / 01",
    title: "Systems & IT Infrastructure Support",
    body: "Custom Software Integration, Cloud Architecture Support, Systems Administration, Network Infrastructure Modernization, and Cyber-Auditing Support for NATO/EU allied installations.",
    testid: "capability-card-it",
  },
  {
    icon: PackageSearch,
    tag: "DIV / 02",
    title: "Tactical Equipment & Dual-Use Sourcing",
    body: "Strategic procurement, supply chain management, and international distribution of non-lethal tactical gear, field communications equipment, ruggedized electronics, and auxiliary power systems.",
    testid: "capability-card-sourcing",
  },
  {
    icon: Handshake,
    tag: "DIV / 03",
    title: "Federal Procurement & Subcontracting Facilitation",
    body: "Serving as a trusted European Prime Integrator & Subcontractor. Local vendor sourcing, cross-border compliance coordination, and Joint Venture teaming for US Prime Contractors operating in Eastern Europe.",
    testid: "capability-card-subcontracting",
  },
  {
    icon: Fuel,
    tag: "DIV / 04",
    title: "Fuel Supply & Distribution",
    body: "Bulk wholesale and logistics of petroleum products, diesel, and liquid fuels for industrial and defence applications — terminal-to-site delivery under NAICS 424710 / 424720.",
    testid: "capability-card-fuel",
  },
  {
    icon: Zap,
    tag: "DIV / 05",
    title: "Industrial & Power Infrastructure Projects",
    body: "Management and execution of large-scale infrastructure, power distribution, substations, and energy facilities — from site works and procurement through commissioning and handover.",
    testid: "capability-card-infrastructure",
  },
  {
    icon: Truck,
    tag: "DIV / 06",
    title: "Logistics & Supply Chain Solutions",
    body: "Specialized transport coordination, procurement support, and complex supply chain logistics for international and governmental projects across EU, NATO and OCONUS theaters.",
    testid: "capability-card-logistics",
  },
];

export default function Capabilities() {
  return (
    <section id="capabilities" className="py-16 md:py-24 lg:py-28 bg-[#0D0F12]" data-testid="capabilities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7 }}
          className="mb-8 md:mb-12"
        >
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-amber-600 mb-3 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-amber-600" /> 01 / Core Capabilities
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl text-slate-50">
            Six Divisions. One Mission Thread.
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl text-sm">
            Technology, sourcing and teaming — now extended with fuel supply, industrial power infrastructure, and end-to-end logistics for government and defence programmes.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-800 border border-slate-800" data-testid="capabilities-grid">
          {CARDS.map((card, i) => (
            <motion.article
              key={card.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              whileHover={{ y: -4 }}
              className="group bg-[#14171A] p-5 sm:p-7 lg:p-8 flex flex-col hover:bg-[#171B20] transition-colors duration-300"
              data-testid={card.testid}
            >
              <div className="flex items-start justify-between mb-5 sm:mb-8">
                <span className="w-10 h-10 border border-slate-700 flex items-center justify-center group-hover:border-amber-600/60 transition-colors duration-300">
                  <card.icon className="w-5 h-5 text-blue-500 group-hover:text-amber-600 transition-colors duration-300" />
                </span>
                <span className="font-mono text-[10px] tracking-[0.25em] text-slate-600 group-hover:text-amber-600/80 transition-colors duration-300">
                  {card.tag}
                </span>
              </div>
              <h3 className="font-display font-bold uppercase tracking-tight text-base md:text-lg text-slate-50 mb-3 leading-snug">
                {card.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed flex-1">{card.body}</p>
              <a
                href="#contact"
                className="mt-5 sm:mt-7 inline-flex items-center gap-2 min-h-[44px] sm:min-h-0 font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400 sm:text-slate-500 group-hover:text-amber-600 transition-colors duration-300"
                data-testid={`${card.testid}-link`}
              >
                Request Capability Brief
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
